'use client';

import React from 'react';

/**
 * Click-to-enlarge for help-article images marked `lightbox` in the editor.
 *
 * ONE delegated listener for the whole article rather than a client component
 * per image: a screenshot-heavy page can carry dozens of images (one help
 * article references 40 media URLs), and hydrating a component for each would
 * cost far more than the feature is worth.
 *
 * Server-rendered markup stays plain — the <figure> carries data-lightbox and a
 * data-full-src; if this never hydrates, the image still renders and the page is
 * fully readable. Progressive enhancement, not a dependency.
 */
export function ImageLightbox() {
  const [open, setOpen] = React.useState<{ src: string; alt: string } | null>(null);
  const closeRef = React.useRef<HTMLButtonElement | null>(null);
  const lastFocused = React.useRef<Element | null>(null);

  React.useEffect(() => {
    function onClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const trigger = target?.closest?.('[data-lightbox="true"]') as HTMLElement | null;
      if (!trigger) return;
      const img = trigger.querySelector('img');
      const src = trigger.getAttribute('data-full-src') || img?.getAttribute('src') || '';
      if (!src) return;
      event.preventDefault();
      lastFocused.current = document.activeElement;
      setOpen({ src, alt: img?.getAttribute('alt') || '' });
    }

    // Keyboard parity: the trigger is a <button>, so Enter/Space already fire
    // click. This only handles Escape while the overlay is open.
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(null);
    }

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Lock scroll while open, move focus to the close button, and restore focus on
  // close — otherwise a keyboard user is dumped back at the top of the document.
  React.useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      (lastFocused.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={open.alt || 'Enlarged image'}
      data-testid="image-lightbox"
      onClick={() => setOpen(null)}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '2rem',
      }}
    >
      <button
        ref={closeRef}
        type="button"
        aria-label="Close enlarged image"
        onClick={() => setOpen(null)}
        style={{
          position: 'absolute', top: '1rem', right: '1rem',
          width: 40, height: 40, borderRadius: '50%', border: 0,
          background: 'rgba(255,255,255,0.15)', color: '#fff',
          fontSize: 22, lineHeight: 1, cursor: 'pointer',
        }}
      >
        ×
      </button>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={open.src}
        alt={open.alt}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }}
      />
    </div>
  );
}
